<template>
  <div
    v-show="skeletonHeight || imageLoaded"
    ref="skeletonElement"
    class="document-view-content-page"
  >
    <PageSkeletonLoader v-if="showLoader" :page-height="skeletonHeight" />
    <div v-show="!showLoader">
      <PageSkeletonLoader
        v-if="imageError"
        :page-height="skeletonHeight"
        :timeout="3000"
        :timeout-message="t('this-page-is-taking-longer-than-usual-try-refresh')"
      />
      <img
        v-show="imageLoaded"
        :src="thumbUrl"
        :alt="imageDescription(page.pageNum)"
        data-external="doc-view-doc-content-page-loaded"
        class="document-content-page-loaded"
        @load="handleImageLoaded"
        @error="handleImageError"
      />
      <template v-if="showFields">
        <ViewField
          v-for="(field, index) in fields"
          :key="field.id"
          :data-external="`view-field-container(${index})`"
          :field="field"
          :sender="sender"
          :recipients="recipients"
        />
      </template>
    </div>
  </div>
</template>
<script lang="ts">
import { useDebounceFn, useIntersectionObserver } from '@vueuse/core';
import type { PropType } from 'vue';
import { computed, defineComponent, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ViewField from '@getaccept/lib-shared/src/document-view/components/ViewField.vue';
import * as FieldPlacementHelper from '@getaccept/lib-shared/src/fields/helpers/field-placement.helper';
import type { Field } from '@getaccept/lib-shared/src/fields/types/field';
import PageSkeletonLoader from '@getaccept/lib-shared/src/skeleton-loader/components/PageSkeletonLoader.vue';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
export default defineComponent({
  components: {
    PageSkeletonLoader,
    ViewField,
  },
  props: {
    page: {
      type: Object as PropType<DocumentPage>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      default: () => ({}),
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      default: () => [],
    },
    pageWidth: {
      type: Number,
      default: 0,
    },
  },
  emits: ['visible'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const imageError = ref(false);
    const imageLoaded = ref(false);
    const fields = ref<Field[]>([]);
    const skeletonElement = ref<HTMLElement | null>(null);
    const thumbUrl = computed(() => props.page.thumbUrl);

    const skeletonHeight = computed(() =>
      props.page && props.page.pageWidth && props.page.pageHeight
        ? (props.pageWidth / props.page.pageWidth) * props.page.pageHeight
        : 900
    );

    const showLoader = computed(
      () => !imageError.value && (!props.page.thumbUrl || !imageLoaded.value)
    );

    const showFields = computed(() => !!imageLoaded.value && !imageError.value);

    const getFields = (_fields: Field[], pageWidth: number) =>
      _fields.map((field: Field) => ({
        ...field,
        position: FieldPlacementHelper.calculateFieldPositionForPlacement(
          field.position,
          pageWidth
        ),
      }));

    const setFields = () => {
      if (props.page?.fields?.length) {
        fields.value = getFields(props.page.fields, props.pageWidth);
      }
    };

    useIntersectionObserver(
      skeletonElement.value,
      ([{ isIntersecting }]) => {
        if (!isIntersecting) {
          return;
        }
        emit('visible', props.page.id);
      },
      {
        threshold: 0.1,
      }
    );
    watch(() => props.pageWidth, setFields, { immediate: true });

    const handleVisibility = (isVisible: boolean) => {
      if (!isVisible) {
        return;
      }
      emit('visible', props.page.id);
    };

    const debounceHandleVisibility = useDebounceFn(handleVisibility, 200);

    const imageDescription = (pageNumber: number) =>
      t('document-page', { 'page-number': pageNumber });

    const handleImageError = () => {
      imageError.value = true;
    };

    const handleImageLoaded = () => {
      imageLoaded.value = true;
    };

    return {
      t,
      skeletonHeight,
      imageLoaded,
      debounceHandleVisibility,
      showLoader,
      imageError,
      thumbUrl,
      imageDescription,
      handleImageLoaded,
      handleImageError,
      showFields,
      fields,
      handleVisibility,
      skeletonElement,
    };
  },
});
</script>
<style lang="scss" scoped>
.document-view-content-page {
  user-select: none;
  position: relative;
  background: var(--white);
  line-height: 0;
  width: 100%;

  .document-content-page-loaded {
    width: 100%;
    object-fit: cover;
    pointer-events: none;
  }
}
</style>
